import { forkJoin, from, Observable, of } from 'rxjs';
import { map, mergeMap, toArray } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { AreaLines3D, AreaLinesIV7, AreaLinesNX, Model3DData, PointSet, PointSet_IV7, PointSet_NX, StereoReconstructionResult, Vertices2Df } from '../../_objects/remeasurement';
import { MEASUREMENT } from '../../_offline/electron.constants';
import { InspectionImageSchema } from '../../_offline/models/inspectionImage.schema';
import { OfflineMeasurementService } from '../../_offline/services/offline-measurement.service';
import { InspectionImageDbService } from '../database/inspection-image-db.service';
import { StereoMeasurementContractService } from '../interfaces/stereo-measurement-contract.service';

@Injectable({
  providedIn: 'root'
})
export class StereoMeasurementImageOfflineService implements StereoMeasurementContractService {

  constructor(
    private inspectionImageDbService: InspectionImageDbService,
    private offlineMeasurementService: OfflineMeasurementService
  ) { }

  getAndGenerationOfMeasureInfo(inspectionId: string, inspectionImageId: string): Observable<any> {
    // 既存の StereoReconstructionResult を MeasurementIntermediateInfoSchema から読み込む。
    // DLL バージョンを取得する。
    // DLL バージョンと既存の StereoReconstructionResult のバージョンが一致 かつ InternalData が存在する場合は、 StereoReconstructionResult を返す。
    // それ以外の場合は reconstruct を実行する。
    // 参考 : <https://ossa-cloud.visualstudio.com/OSD%20Visol%20Solution/_git/App-RviDataApp?path=/src/Controllers/MeasurementApiController.cs&version=GBdev&line=63&lineEnd=88&lineStartColumn=1&lineEndColumn=10&lineStyle=plain&_a=contents>

    let param: IMeasurementParam = null;

    return this.getMeasurementParam(inspectionId, inspectionImageId, '')
      .pipe(
        mergeMap(x => {
          param = x;

          return forkJoin([
            this.offlineMeasurementService.getLibraryVersion(param.schema.imageType),
            this.offlineMeasurementService.getReconstruction(inspectionImageId),
            this.offlineMeasurementService.isExistsInternalData(param.internalDataFilePath)
          ]);
        }),
        mergeMap(x => {
          const libraryVersion: string = x[0];
          const reconstructionResult: StereoReconstructionResult = x[1];
          const isExistsInternalData: boolean = x[2];
          const imageFilePath: string = param.schema.localFilePath.replace(/\//g, '\\');

          if ((libraryVersion === reconstructionResult?.libVersion) && isExistsInternalData) {
            return of(reconstructionResult);
          }

          return this.offlineMeasurementService.reconstruct(inspectionImageId, param.schema.imageType, imageFilePath, param.internalDataFilePath, param.pointCloudFilePath, param.reconstructionFilePath);
        })
      );
  }

  measureDotCoordinate3dDataSet(inspectionId: string, inspectionImageId: string, vertices2Df: Vertices2Df): Observable<any> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.getPointSet.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.getPointSet(x.schema.imageType, x.internalDataFilePath, x.pointCloudFilePath, x.outputFilePath, vertices2Df))
      );
  }
  getDistortionReproductionCurve3d(inspectionId: string, inspectionImageId: string, pointSets: Vertices2Df[], lineType: number, lr: number): Observable<any> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.getPolyline.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.getPolyline(x.schema.imageType, x.internalDataFilePath, x.outputFilePath, pointSets, lineType, lr))
      );
  }
  getTwoPointMeasure3d(inspectionId: string, inspectionImageId: string, pointSets: PointSet[]): Observable<any> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.measureDistance.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.measureDistance(x.schema.imageType, x.internalDataFilePath, x.outputFilePath, pointSets))
      );
  }
  getLineReferenceMeasure3d(inspectionId: string, inspectionImageId: string, pointSets: PointSet[]): Observable<any> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.measurePointToLine.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.measurePointToLine(x.schema.imageType, x.internalDataFilePath, x.outputFilePath, pointSets))
      );
  }
  getSurfaceReferenceMeasure3d(inspectionId: string, inspectionImageId: string, pointSets: PointSet[], model: Model3DData): Observable<any> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.measureDepth.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.measureDepth(x.schema.imageType, x.internalDataFilePath, x.outputFilePath, x.pointCloudFilePath, pointSets))
      );
  }

  measureDotCoordinateIV7DataSet(inspectionId: string, inspectionImageId: string, pointSets: Vertices2Df): Observable<any> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.getPointSet.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.getPointSet(x.schema.imageType, x.internalDataFilePath, x.pointCloudFilePath, x.outputFilePath, pointSets))
      );
  }

  getDistortionReproductionCurveIV7(inspectionId: string, inspectionImageId: string, pointSets: Vertices2Df[], lineType: number, lr: number): Observable<any> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.getPolyline.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.getPolyline(x.schema.imageType, x.internalDataFilePath, x.outputFilePath, pointSets, lineType, lr))
      );
  }

  getTwoPointMeasureIV7(inspectionId: string, inspectionImageId: string, pointSets: PointSet_IV7[]): Observable<any> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.measureDistance.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.measureDistance(x.schema.imageType, x.internalDataFilePath, x.outputFilePath, pointSets))
      );
  }

  getLineReferenceMeasureIV7(inspectionId: string, inspectionImageId: string, pointSets: PointSet_IV7[]): Observable<any> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.measurePointToLine.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.measurePointToLine(x.schema.imageType, x.internalDataFilePath, x.outputFilePath, pointSets))
      );
  }

  getSurfaceReferenceMeasureIV7(inspectionId: string, inspectionImageId: string, pointSets: PointSet_IV7[]): Observable<any> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.measureDepth.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.measureDepth(x.schema.imageType, x.internalDataFilePath, x.outputFilePath, x.pointCloudFilePath, pointSets))
      );
  }

  getAreaTotalLengthMeasure(inspectionId: string, inspectionImageId: string, pointSets: AreaLines3D): Observable<any> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.measureArea.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.measureArea(x.schema.imageType, x.internalDataFilePath, x.outputFilePath, pointSets.pointSets, pointSets.pntCount))
      );
  }

  getAreaMeasure(inspectionId: string, inspectionImageId: string, pointSets: AreaLinesIV7): Observable<any> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.measureArea.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.measureArea(x.schema.imageType, x.internalDataFilePath, x.outputFilePath, pointSets.pointSets, pointSets.pntCount))
      );
  }

  getOverallLengthMeasure(inspectionId: string, inspectionImageId: string, pointSets: AreaLinesIV7): Observable<any> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.measureLines.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.measureLines(x.schema.imageType, x.internalDataFilePath, x.outputFilePath, pointSets.pointSets, pointSets.pntCount))
      );
  }

  measureDotCoordinateNXDataSet(inspectionId: string, inspectionImageId: string, pointSets: Vertices2Df): Observable<any> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.getPointSet.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.getPointSet(x.schema.imageType, x.internalDataFilePath, x.pointCloudFilePath, x.outputFilePath, pointSets))
      );
  }

  getDistortionReproductionCurveNX(inspectionId: string, inspectionImageId: string, pointSets: Vertices2Df[], lineType: number, lr: number): Observable<any> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.getPolyline.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.getPolyline(x.schema.imageType, x.internalDataFilePath, x.outputFilePath, pointSets, lineType, lr))
      );
  }

  getTwoPointMeasureNX(inspectionId: string, inspectionImageId: string, pointSets: PointSet_NX[]): Observable<any> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.measureDistance.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.measureDistance(x.schema.imageType, x.internalDataFilePath, x.outputFilePath, pointSets))
      );
  }

  getLineReferenceMeasureNX(inspectionId: string, inspectionImageId: string, pointSets: PointSet_NX[]): Observable<any> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.measurePointToLine.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.measurePointToLine(x.schema.imageType, x.internalDataFilePath, x.outputFilePath, pointSets))
      );
  }

  getSurfaceReferenceMeasureNX(inspectionId: string, inspectionImageId: string, pointSets: PointSet_NX[]): Observable<any> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.measureDepth.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.measureDepth(x.schema.imageType, x.internalDataFilePath, x.outputFilePath, x.pointCloudFilePath, pointSets))
      );
  }

  getAreaMeasureNX(inspectionId: string, inspectionImageId: string, pointSets: AreaLinesNX): Observable<any> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.measureArea.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.measureArea(x.schema.imageType, x.internalDataFilePath, x.outputFilePath, pointSets.pointSets, pointSets.pntCount))
      );
  }

  getOverallLengthMeasureNX(inspectionId: string, inspectionImageId: string, pointSets: AreaLinesNX): Observable<any> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.measureLines.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.measureLines(x.schema.imageType, x.internalDataFilePath, x.outputFilePath, pointSets.pointSets, pointSets.pntCount))
      );
  }

  get3DModelData(inspectionId: string, inspectionImageId: string): Observable<Model3DData> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, '')
      .pipe(
        mergeMap(x => this.offlineMeasurementService.get3DModelData(x.pointCloudFilePath))
      );
  }

  getImageBinaryUrl(inspectionId: string, inspectionImageId: string): Promise<string> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, '')
      .pipe(
        mergeMap(x => this.offlineMeasurementService.get3DModelImageDataFile(x.schema.imageType, x.pointCloudFilePath))
      )
      .toPromise();
  }

  async measureDotCoordinateIV7DataSetAsync(inspectionId: string, inspectionImageId: string, pointSets: Vertices2Df): Promise<any> {
    return await this.measureDotCoordinateIV7DataSet(inspectionId, inspectionImageId, pointSets).toPromise();
  }

  async measureDotCoordinate3dDataSetAsync(inspectionId: string, inspectionImageId: string, vertices2Df: Vertices2Df, model: Model3DData): Promise<any> {
    return await this.measureDotCoordinate3dDataSet(inspectionId, inspectionImageId, vertices2Df).toPromise();
  }

  async measureDotCoordinateNXDataSetAsync(inspectionId: string, inspectionImageId: string, vertices2Df: Vertices2Df): Promise<any> {
    return await this.measureDotCoordinateNXDataSet(inspectionId, inspectionImageId, vertices2Df).toPromise();
  }

  async getDistortionReproductionCurveIV7Async(inspectionId: string, inspectionImageId: string, pointSets: Vertices2Df[], lineType: number, lr: number): Promise<any> {
    return await this.getDistortionReproductionCurveIV7(inspectionId, inspectionImageId, pointSets, lineType, lr).toPromise();
  }

  async getDistortionReproductionCurve3dAsync(inspectionId: string, inspectionImageId: string, vertices2Df: Vertices2Df[], lineType: number, lr: number): Promise<any> {
    return await this.getDistortionReproductionCurve3d(inspectionId, inspectionImageId, vertices2Df, lineType, lr).toPromise();
  }

  async getDistortionReproductionCurveNXAsync(inspectionId: string, inspectionImageId: string, vertices2Df: Vertices2Df[], lineType: number, lr: number): Promise<any> {
    return await this.getDistortionReproductionCurveNX(inspectionId, inspectionImageId, vertices2Df, lineType, lr).toPromise();
  }

  measureMissingCorner(inspectionId: string, inspectionImageId: string, pointSets: PointSet[], model: Model3DData) {
    return this.getMeasurementParam(inspectionId, inspectionImageId, this.offlineMeasurementService.measureMissingCorner.name)
      .pipe(
        mergeMap(x => this.offlineMeasurementService.measureMissingCorner(x.schema.imageType, x.internalDataFilePath, x.outputFilePath, x.pointCloudFilePath, pointSets))
      );
  }
  /**
   * 対象の MeasurementIntermediateInfoSchema、 InternalData、 PointCloud ファイルを削除する。
   *
   * @param inspectionId 検査 ID。
   * @param inspectionImageId 検査画像 ID。
   * @returns true … 成功。
   */
  public deleteMeasurementIntermediateInfo(inspectionId: string, inspectionImageId: string): Observable<boolean> {
    return this.getMeasurementParam(inspectionId, inspectionImageId, '')
      .pipe(
        mergeMap(x => this.offlineMeasurementService.deleteMeasurementIntermediateInfo(inspectionImageId, this.getMeasurementDirectoryPath(inspectionId, inspectionImageId)))
      );
  }

  /**
   * 対象の MeasurementIntermediateInfoSchema、 InternalData、 PointCloud ファイルを削除する。
   *
   * @param inspectionId 検査 ID。
   * @param inspectionImageIds 検査画像 ID の配列。
   * @returns true … 成功。
   */
  public deleteMeasurementIntermediateInfos(inspectionId: string, inspectionImageIds: Array<string>): Observable<boolean> {
    return from(inspectionImageIds)
      .pipe(
        mergeMap(inspectionImageId => of(this.getMeasurementDirectoryPath(inspectionId, inspectionImageId))),
        toArray(),
        mergeMap(measurementDirectoryPaths => this.offlineMeasurementService.deleteMeasurementIntermediateInfos(inspectionImageIds, measurementDirectoryPaths))
      );
  }

  /**
   * Measurement ローカルデータディレクトリーパスを返す。
   *
   * @param inspectionId 検査 ID。
   * @param inspectionImageId 検査画像 ID。
   * @returns 対象のディレクトリーパス。
   */
  private getMeasurementDirectoryPath(inspectionId: string, inspectionImageId: string): string {
    return ['inspections', inspectionId, MEASUREMENT.DIRECTORY_NAME, inspectionImageId].join('\\').replace(/\//g, '\\');
  }

  /**
   * Measurement で使用するパラメータを返す。
   *
   * @param inspectionId 検査 ID。
   * @param inspectionImageId 検査画像 ID。
   * @param outputFileNameWithoutExtension 拡張子無しの出力ファイル名。
   * @returns 対象のデータ、ファイルパスを返す。
   */
  private getMeasurementParam(inspectionId: string, inspectionImageId: string, outputFileNameWithoutExtension: string): Observable<IMeasurementParam> {
    return this.inspectionImageDbService.get(inspectionId, inspectionImageId)
      .pipe(
        map(inspectionImageSchema => {
          const measurementDirectoryPath: string = this.getMeasurementDirectoryPath(inspectionId, inspectionImageId);
          const result: IMeasurementParam = {
            schema: inspectionImageSchema,
            internalDataFilePath: [measurementDirectoryPath, MEASUREMENT.INTERNAL_DATA_FILE].join('\\'),
            pointCloudFilePath: [measurementDirectoryPath, MEASUREMENT.POINT_CLOUD_FILE].join('\\'),
            reconstructionFilePath: [measurementDirectoryPath, MEASUREMENT.RECONSTRUCTION_FILE].join('\\'),
            outputFilePath: [measurementDirectoryPath, `${outputFileNameWithoutExtension}.json`].join('\\')
          };
          return result;
        })
      );
  }

}

/**
 * Measurement パラメータ用インターフェイス。
 */
interface IMeasurementParam {

  /** InspectionImageSchema のデータ。 */
  schema: InspectionImageSchema,

  /** InternalData ファイルパス。 */
  internalDataFilePath: string,

  /** PointCloud ファイルパス。 */
  pointCloudFilePath: string,

  /** Reconstruct 結果ファイルパス。 */
  reconstructionFilePath: string;

  /** 出力結果ファイルパス。 */
  outputFilePath: string

}
